import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ExportFormatCode } from '@/core/models/practice.models';
import {
  formatStatement,
  type CashFlowReport,
  type StatementLine,
  type TrialBalanceOption,
} from '@/core/models/reports.models';
import { notifyError } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { reportsService } from '@/core/services/reports';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import './TaxreturnCashflows.scss';

/** "dd-MMM-yyyy" (the API's display format) or ISO "yyyy-MM-dd" to a time value; null when it cannot be read. */
function parseDisplayDate(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const display = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(value.trim());
  if (display) {
    const month = months.indexOf(display[2].toLowerCase());
    return month < 0 ? null : Date.UTC(Number(display[3]), month, Number(display[1]));
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  return iso ? Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : null;
}

const isBold = (line: StatementLine) => line.style === 'heading' || line.style === 'subtotal' || line.style === 'total';

/**
 * Statement of cash flows for the selected trial balance (Angular TaxreturnCashflowsComponent),
 * `/home/taxreturns/reports-cashflows`. "Select Reference" is the comparative (previous) trial balance; left empty,
 * the most recent earlier trial balance is used and shown once generated.
 */
export function TaxreturnCashflows() {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  const [trialBalances, setTrialBalances] = useState<TrialBalanceOption[]>([]);
  const [selectedTrialBalanceId, setSelectedTrialBalanceId] = useState<string | null>(null);
  const [selectedReferenceId, setSelectedReferenceId] = useState<string | null>(null);
  const [clientName, setClientName] = useState(client?.name ?? '');
  const [currentHeader, setCurrentHeader] = useState('');
  const [previousHeader, setPreviousHeader] = useState('');
  const [lines, setLines] = useState<StatementLine[]>([]);
  const report = useRef<CashFlowReport | null>(null);

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    let cancelled = false;
    reportsService.getTrialBalances(client.id).then(
      (rows) => {
        if (!cancelled) setTrialBalances(rows);
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * #178: lists only trial balances that end before the selected one starts - the earlier years the API accepts as the
   * comparative - so the preparer chooses between them instead of one being picked for them.
   */
  const referenceOptions = useMemo(() => {
    const current = trialBalances.find((t) => t.id === selectedTrialBalanceId);
    const currentStart = current ? parseDisplayDate(current.fromDate) : null;
    return trialBalances.filter((t) => {
      if (t.id === selectedTrialBalanceId) {
        return false;
      }
      const end = parseDisplayDate(t.toDate);
      return !currentStart || !end || end < currentStart;
    });
  }, [trialBalances, selectedTrialBalanceId]);

  /**
   * #179: a new trial balance clears the reference and the statement generated for the previous choice, so the
   * comparative is chosen (or defaulted) again for the new one rather than the old one lingering.
   */
  const onTrialBalanceChange = (id: string | null) => {
    setSelectedTrialBalanceId(id);
    setSelectedReferenceId(null);
    report.current = null;
    setLines([]);
    setCurrentHeader('');
    setPreviousHeader('');
  };

  /** Generate is running (stops a second click). */
  const [isGenerating, setIsGenerating] = useState(false);
  const generating = useRef(false);

  const generate = async () => {
    if (!selectedTrialBalanceId) {
      notifyError('Select a trial balance to generate the statement.');
      return;
    }
    if (generating.current) {
      return;
    }
    generating.current = true;
    setIsGenerating(true);
    try {
      const generated = await reportsService.getCashFlow(selectedTrialBalanceId, selectedReferenceId);
      report.current = generated;
      setClientName(generated.clientName);
      setCurrentHeader(generated.currentHeader);
      setPreviousHeader(generated.previousHeader);
      setSelectedReferenceId(generated.previousTrialBalanceId);
      setLines(generated.lines);
    } catch (error) {
      notifyError(error);
    } finally {
      generating.current = false;
      setIsGenerating(false);
    }
  };

  const exportReport = async (format: ExportFormatCode) => {
    const current = report.current;
    if (!current) {
      notifyError('Generate the statement before exporting it.');
      return;
    }
    try {
      await reportsService.exportCashFlow(current.trialBalanceId, current.previousTrialBalanceId, format);
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <SideMenuPage menu="reports">
      {/* Design review 2 Nov 2026: Generate is the page's action, so it sits top-right in the sticky page header; the pickers stay under it. */}
      <PageHeader title="Cash Flows">
        <Button className="action-button primary-button" busy={isGenerating} onClick={() => void generate()}>
          <Icon name="sync" />
          <span>Generate</span>
        </Button>
      </PageHeader>
      <div className="content-body tr-cashflows">
        <div className="content-header mb-0">
          <div className="content-header-right mb-1">
            <div className="text-gray font-size-14 fw-bold">
              Select Trial Balance<span className="text-red">*</span>
            </div>
            <Select
              className="w-270"
              items={trialBalances}
              bindLabel="label"
              bindValue="id"
              placeholder="Select Trial Balance"
              value={selectedTrialBalanceId}
              onChange={(value) => onTrialBalanceChange(value as string | null)}
            />
            <Select
              className="w-270"
              items={referenceOptions}
              bindLabel="label"
              bindValue="id"
              placeholder="Select Reference"
              value={selectedReferenceId}
              onChange={(value) => setSelectedReferenceId(value as string | null)}
            />
            <Button className="light-bg" onClick={() => navigate('/home/taxreturns/tasks-createtrialbalance')}>
              <Icon name="add" />
            </Button>
          </div>
        </div>
        <div className="content-header pt-1 mb-0 hr-top">
          <div className="content-header-left mb-1">
            <div className="text-gray font-size-18 fw-normal">
              Statement of Cash Flows : <span className="fw-bold">{clientName}</span>
            </div>
          </div>
          <div className="content-header-right mb-1">
            <div className="toolbar">
              <Button title="Export to Excel" onClick={() => void exportReport(ExportFormatCode.Excel)}>
                <Icon name="border_all" className="icon-color-xls" />
              </Button>
              <Button title="Export to CSV" onClick={() => void exportReport(ExportFormatCode.Csv)}>
                <Icon name="description" outlined className="icon-color-xls" />
              </Button>
              <Button title="Export to PDF" onClick={() => void exportReport(ExportFormatCode.Pdf)}>
                <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
              </Button>
            </div>
          </div>
        </div>

        <div className="sidenav-table sidenav-table-input wp-100">
          <table
            className="cst-table"
            aria-describedby="Cash flow Report Table"
            cellSpacing={0}
            cellPadding={10}
            width="100%"
          >
            <tbody>
              <tr>
                <th>
                  &nbsp;
                  <br />
                  &nbsp;
                </th>
                <th>
                  Notes
                  <br />
                  &nbsp;
                </th>
                <th align="right">
                  {currentHeader}
                  <br />
                  {currentHeader ? '£' : ''}
                </th>
                <th align="right">
                  {previousHeader}
                  <br />
                  {previousHeader ? '£' : ''}
                </th>
              </tr>
              {lines.map((line, index) => (
                <tr key={index}>
                  <td className={isBold(line) ? 'fw-bold' : undefined}>{line.label}</td>
                  <td>{line.note}&nbsp;</td>
                  <td align="right" className={isBold(line) ? 'fw-bold' : undefined}>
                    {formatStatement(line.current)}&nbsp;
                  </td>
                  <td align="right" className={isBold(line) ? 'fw-bold' : undefined}>
                    {formatStatement(line.previous)}&nbsp;
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SideMenuPage>
  );
}
